import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import './Home.css';

const pillars = [
  {
    title: 'Stays on your floor',
    desc: 'MyAlpha runs as a rack appliance or on your qualified NVIDIA box. Inference, fine-tunes, and documents never leave your network unless you say so.',
  },
  {
    title: 'Tuned to your industry',
    desc: 'We ship models and workflows shaped for how your sector actually talks — SOPs, codes, parts, contracts, clinical notes — not a generic chatbot.',
  },
  {
    title: 'You set the policy',
    desc: 'No cloud provider filter deciding what is “allowed.” MyAlpha uses enterprise models you configure. Compliance and tone are yours to define.',
  },
];

const steps = [
  { num: '01', title: 'Scope', desc: 'Map data boundaries, use cases, and industry vocabulary.' },
  { num: '02', title: 'Load', desc: 'Deploy MyAlpha Rack or MyAlpha on your NVIDIA hardware.' },
  { num: '03', title: 'Tune', desc: 'Industry adapters, retrieval over your private corpus, access controls.' },
  { num: '04', title: 'Own', desc: 'Your team runs it; we stay available for updates and expansion.' },
];

const industries = [
  { name: 'Automotive', slug: 'automotive' },
  { name: 'Heavy Equipment', slug: 'heavy-equipment' },
  { name: 'Medical', slug: 'medical' },
  { name: 'Supply Chain', slug: 'supply-chain' },
  { name: 'Legal', slug: 'legal' },
  { name: 'Manufacturing', slug: 'manufacturing' },
  { name: 'Energy', slug: 'energy-and-utilities' },
  { name: 'Financial Services', slug: 'financial-services' },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
  },
};

export const Home = () => {
  return (
    <motion.div
      className="home"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <section className="hero">
        <div className="hero-atmosphere" aria-hidden="true" />
        <motion.div
          className="hero-visual"
          variants={itemVariants}
        >
          <img
            src="/myalpha-rack.png"
            alt="MyAlpha rack server appliance"
            className="hero-rack-img"
          />
        </motion.div>
        <div className="hero-inner">
          <motion.div className="hero-badge" variants={itemVariants}>
            <span className="badge-dot"></span>
            On-prem · Industry-tuned · Customer-controlled
          </motion.div>

          <motion.p className="hero-brand" variants={itemVariants}>
            MyAlpha
          </motion.p>

          <motion.p className="hero-meaning" variants={itemVariants}>
            Alpha is your company’s secret sauce — the proprietary knowledge,
            judgment, and edge that competitors can’t copy.
          </motion.p>

          <motion.h1 className="hero-title" variants={itemVariants}>
            Your AI. Your building. Your rules.
          </motion.h1>

          <motion.p className="hero-subtitle" variants={itemVariants}>
            MyAlpha is a local LLM appliance for companies that need industry-specific
            intelligence without sending data to the cloud — and without third-party
            content filters rewriting how your teams work.
          </motion.p>

          <motion.div className="hero-cta" variants={itemVariants}>
            <Link to="/contact?source=home-briefing" className="btn btn-primary">
              Request a MyAlpha briefing
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
            <Link to="/myalpha" className="btn btn-secondary">
              See how MyAlpha deploys
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="pillars-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Built for operators, not demos</h2>
          </motion.div>

          <motion.div
            className="pillars-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            {pillars.map((pillar) => (
              <motion.div key={pillar.title} className="pillar" variants={itemVariants}>
                <h3>{pillar.title}</h3>
                <p>{pillar.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="how-section">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>From brief to production stack</h2>
          </motion.div>

          <motion.div
            className="steps-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            {steps.map((step) => (
              <motion.div key={step.num} className="step" variants={itemVariants}>
                <span className="step-num">{step.num}</span>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="industries-preview">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Built for your floor</h2>
            <p>
              Purpose-built for environments where privacy, uptime, and plain-spoken
              answers matter.
            </p>
          </motion.div>

          <motion.div
            className="industry-links"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            {industries.map((item) => (
              <motion.div key={item.slug} variants={itemVariants}>
                <Link to={`/industries#${item.slug}`} className="industry-link">
                  {item.name}
                </Link>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="industries-cta"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <Link to="/industries" className="btn btn-secondary">
              Explore industry packs
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="cta-text">
              <h2>Ready to keep AI inside the fence?</h2>
              <p>
                Tell us your industry and whether you want a turnkey MyAlpha Rack or MyAlpha
                loaded on your NVIDIA box.
              </p>
            </div>
            <Link to="/contact?source=home-cta" className="btn btn-primary btn-lg">
              Talk to sales
            </Link>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default Home;
